import React, { useMemo, useState } from "react";
import { isGridQuestion, getGridCells, roleOfVarName } from "./dataPrepOps";
import { resolveKeysToVarNames } from "./VariablePicker";

function singleVarKeys(keys, dataGroup) {
  return (keys || []).filter((k) => (dataGroup[k]?.varName || []).length === 1);
}

function FilterBuilder({ onAddStep }) {
  const [logic, setLogic] = useState("");
  return (
    <div className="prep-builder">
      <label>Filter logic</label>
      <textarea
        className="prep-textarea"
        rows={3}
        placeholder="e.g. S1=1 OR S1=2   ·   age>=18 AND age<=65   ·   region=1-3"
        value={logic}
        onChange={(e) => setLogic(e.target.value)}
      />
      <p className="analysis-muted">Same syntax as Banner/Crosstab filters: equality, ranges (a-b), AND/OR, comparison operators.</p>
      <button
        className="prep-add-btn"
        disabled={!logic.trim()}
        onClick={() => { onAddStep({ type: "filter", logic: logic.trim() }); setLogic(""); }}
      >
        Add Filter to Recipe
      </button>
    </div>
  );
}

function RecodeBuilder({ dataGroup, variableTypes, currentSelectedVariables, onAddStep }) {
  const [sourceKey, setSourceKey] = useState("");
  const [mode, setMode] = useState("reverse");
  const [rules, setRules] = useState([{ from: "", to: "" }]);
  const [missingValues, setMissingValues] = useState("");
  const [newName, setNewName] = useState("");

  const options = useMemo(() => singleVarKeys(currentSelectedVariables, dataGroup), [currentSelectedVariables, dataGroup]);
  const sourceRaw = resolveKeysToVarNames(sourceKey ? [sourceKey] : [], dataGroup)[0];
  const sourceRole = sourceRaw ? roleOfVarName(sourceRaw, variableTypes) : null;

  const defaultName = sourceKey ? `${sourceKey}_recoded` : "";

  const addRule = () => setRules((prev) => [...prev, { from: "", to: "" }]);
  const updateRule = (i, field, val) => setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeRule = (i) => setRules((prev) => prev.filter((_, idx) => idx !== i));

  const parseValues = (s) => s.split(",").map((v) => v.trim()).filter(Boolean).map((v) => (isNaN(Number(v)) ? v : Number(v)));

  const canAdd = sourceKey && (newName || defaultName) && (
    mode === "reverse" ||
    (mode === "missing" && missingValues.trim()) ||
    (mode === "manual" && rules.some((r) => r.from.trim() && r.to.trim() !== ""))
  );

  const handleAdd = () => {
    const finalName = (newName || defaultName).trim();
    const label = dataGroup[sourceKey]?.Qtitle || sourceKey;
    if (mode === "reverse") {
      onAddStep({ type: "recode", sourceKey, newName: finalName, preset: "reverse", label: `${label} (reversed)` });
    } else if (mode === "missing") {
      onAddStep({
        type: "recode", sourceKey, newName: finalName,
        mapping: [{ from: parseValues(missingValues), to: null }],
        label: `${label} (missing recoded)`,
      });
    } else {
      const mapping = rules.filter((r) => r.from.trim()).map((r) => ({ from: parseValues(r.from), to: isNaN(Number(r.to)) ? r.to : Number(r.to) }));
      onAddStep({ type: "recode", sourceKey, newName: finalName, mapping, label: `${label} (recoded)` });
    }
    setRules([{ from: "", to: "" }]);
    setMissingValues("");
    setNewName("");
  };

  return (
    <div className="prep-builder">
      <label>Source variable</label>
      <select className="analysis-select" value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
        <option value="">Select a single-variable question...</option>
        {options.map((k) => <option key={k} value={k}>{dataGroup[k]?.Qtitle ? `${k} — ${dataGroup[k].Qtitle}` : k}</option>)}
      </select>
      {sourceKey && sourceRole !== "numeric" && (
        <p className="prep-warning">"{sourceKey}" looks categorical — recoding still works, but reverse-scale expects a numeric scale.</p>
      )}

      <label>Recode mode</label>
      <div className="prep-mode-row">
        <label><input type="radio" checked={mode === "reverse"} onChange={() => setMode("reverse")} /> Reverse scale</label>
        <label><input type="radio" checked={mode === "manual"} onChange={() => setMode("manual")} /> Recode specific values</label>
        <label><input type="radio" checked={mode === "missing"} onChange={() => setMode("missing")} /> Set values to missing</label>
      </div>

      {mode === "reverse" && (
        <p className="analysis-muted">Automatically reverses using the variable's actual min/max (e.g. a 1-5 scale: 1↔5, 2↔4, 3 stays).</p>
      )}

      {mode === "manual" && (
        <div className="prep-mapping-rows">
          {rules.map((r, i) => (
            <div className="prep-mapping-row" key={i}>
              <input placeholder="from (e.g. 1,2)" value={r.from} onChange={(e) => updateRule(i, "from", e.target.value)} />
              <span>→</span>
              <input placeholder="to" value={r.to} onChange={(e) => updateRule(i, "to", e.target.value)} />
              <button onClick={() => removeRule(i)} aria-label="Remove rule">&times;</button>
            </div>
          ))}
          <button className="prep-add-rule-btn" onClick={addRule}>+ Add rule</button>
        </div>
      )}

      {mode === "missing" && (
        <>
          <label>Values to treat as missing</label>
          <input className="analysis-select" placeholder="e.g. 98,99" value={missingValues} onChange={(e) => setMissingValues(e.target.value)} />
        </>
      )}

      <label>New variable name</label>
      <input className="analysis-select" placeholder={defaultName || "e.g. S1_recoded"} value={newName} onChange={(e) => setNewName(e.target.value)} />

      <button className="prep-add-btn" disabled={!canAdd} onClick={handleAdd}>Add Recode to Recipe</button>
    </div>
  );
}

function StackBuilder({ dataGroup, variableTypes, currentSelectedVariables, onAddStep }) {
  const [gridKey, setGridKey] = useState("");
  const [checkedCells, setCheckedCells] = useState({});
  const [idVarName, setIdVarName] = useState("");
  const [valueVarName, setValueVarName] = useState("");
  const [valueRole, setValueRole] = useState("numeric");

  const gridOptions = useMemo(
    () => (currentSelectedVariables || []).filter((k) => isGridQuestion(dataGroup, k)),
    [currentSelectedVariables, dataGroup]
  );
  const cells = useMemo(() => (gridKey ? getGridCells(dataGroup, gridKey) : []), [gridKey, dataGroup]);

  const handleSelectGrid = (key) => {
    setGridKey(key);
    setCheckedCells({});
    setIdVarName(key ? `${key}_dimension` : "");
    setValueVarName(key ? `${key}_value` : "");
    const firstRaw = getGridCells(dataGroup, key)[0]?.name;
    setValueRole(firstRaw ? roleOfVarName(firstRaw, variableTypes) : "numeric");
  };

  const toggleCell = (name) => setCheckedCells((prev) => ({ ...prev, [name]: !prev[name] }));

  const selectedCells = cells.filter((c) => checkedCells[c.name]);
  const canAdd = gridKey && selectedCells.length >= 2 && idVarName.trim() && valueVarName.trim();

  const handleAdd = () => {
    onAddStep({
      type: "stack",
      gridKey,
      cellColumns: selectedCells.map((c) => ({ name: c.name, label: c.label })),
      idVarName: idVarName.trim(),
      valueVarName: valueVarName.trim(),
      idLabel: `${dataGroup[gridKey]?.Qtitle || gridKey}: dimension`,
      valueLabel: `${dataGroup[gridKey]?.Qtitle || gridKey}: value`,
      valueRole,
    });
    setGridKey(""); setCheckedCells({}); setIdVarName(""); setValueVarName("");
  };

  return (
    <div className="prep-builder">
      <label>Grid question</label>
      <select className="analysis-select" value={gridKey} onChange={(e) => handleSelectGrid(e.target.value)}>
        <option value="">Select a grid question...</option>
        {gridOptions.map((k) => <option key={k} value={k}>{dataGroup[k]?.Qtitle ? `${k} — ${dataGroup[k].Qtitle}` : k}</option>)}
      </select>
      {currentSelectedVariables?.length > 0 && gridOptions.length === 0 && (
        <p className="analysis-muted">None of your currently-selected variables look like a grid (multiple cells with row/column structure).</p>
      )}

      {gridKey && (
        <>
          <label>Cells to stack ({selectedCells.length} selected, need 2+)</label>
          <div className="prep-cell-list">
            {cells.map((c) => (
              <label className="prep-cell-row" key={c.name}>
                <input type="checkbox" checked={!!checkedCells[c.name]} onChange={() => toggleCell(c.name)} />
                <span className="prep-cell-label">{c.label}</span>
                <span className="dv-code">{c.name}</span>
              </label>
            ))}
          </div>

          <div className="config-settings-row">
            <div className="analysis-form-field">
              <label>Dimension variable name</label>
              <input className="analysis-select" value={idVarName} onChange={(e) => setIdVarName(e.target.value)} />
            </div>
            <div className="analysis-form-field">
              <label>Value variable name</label>
              <input className="analysis-select" value={valueVarName} onChange={(e) => setValueVarName(e.target.value)} />
            </div>
            <div className="analysis-form-field">
              <label>Value role</label>
              <select className="analysis-select" value={valueRole} onChange={(e) => setValueRole(e.target.value)}>
                <option value="numeric">Numeric</option>
                <option value="categorical">Categorical</option>
              </select>
            </div>
          </div>
          <p className="prep-warning">
            Stacked rows from the same respondent aren't statistically independent — standard errors in Regression/T-Test
            will understate uncertainty. Treat results as exploratory unless that's accounted for elsewhere.
          </p>
        </>
      )}

      <button className="prep-add-btn" disabled={!canAdd} onClick={handleAdd}>Add Stack to Recipe</button>
    </div>
  );
}

/** Renders the builder UI for whichever Data Prep step type is selected in
 * the Technique Library. Adding a step appends it to the cross-cutting
 * recipe (managed by the parent), which stays active across technique switches. */
function DataPrepBuilder({ prepKind, dataGroup, variableTypes, currentSelectedVariables, onAddStep }) {
  if (prepKind === "filter") return <FilterBuilder onAddStep={onAddStep} />;
  if (prepKind === "recode") {
    return (
      <RecodeBuilder
        dataGroup={dataGroup} variableTypes={variableTypes}
        currentSelectedVariables={currentSelectedVariables} onAddStep={onAddStep}
      />
    );
  }
  if (prepKind === "stack") {
    return (
      <StackBuilder
        dataGroup={dataGroup} variableTypes={variableTypes}
        currentSelectedVariables={currentSelectedVariables} onAddStep={onAddStep}
      />
    );
  }
  return null;
}

export default DataPrepBuilder;
